import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { photoService } from '@/services/';
import VueSlider from 'vue-slider-component';
import {
  TimelapseConfiguration,
  StabilizationConfig,
  StabilizationInterpol,
  StabilizationCrop,
  ImageModifier,
  TemporalFilterConfig,
  TemporalFilterMode,
} from '@/models/timelapseconfiguration';
import { Crop, Position } from '@/models/enums';
import { Logger } from 'aws-amplify';
const logger = new Logger('TimelapseSettings');
import { User } from '@/models/user';
import { State } from 'vuex-class';
import { Storage } from 'aws-amplify';
import InfoTip from '@/views/InfoTip.vue';
import KenBurnsEditor from '@/views/KenBurnsEditor.vue';
import CLAHE from '@/views/CLAHE.vue';

interface HTMLInputEvent extends Event {
  target: HTMLInputElement & EventTarget;
}

declare global {
  interface Window {
    webkitAudioContext?: typeof AudioContext;
  }
}

@Component({
  components: {
    VueSlider,
    InfoTip,
    KenBurnsEditor,
    CLAHE,
  },
})
export default class TimelapseSettings extends Vue {
  @Prop({ required: true, type: Object }) private readonly value!: TimelapseConfiguration;
  @Prop({ type: Object, required: false })
  readonly camera?: { id: string; rotate?: number; lastPhoto?: { key?: string } };
  @State('user', { namespace: 'profile' }) private user!: User;

  // Accordéon Overlay existant
  private visible = false;

  // Accordéon Stabilisation (nouveau)
  private stabOpen = false;
  private stabMoreOpen = false;

  private activeModifier: string | null = null;
  private show = true;

  // Exposés au template
  private Crop = Crop;
  private Position = Position;
  private validLogo: boolean | null = null;

  // Prévisualisation
  private previewSrc = ''; // ⬅️ URL de la dernière photo
  private showPreview = false; // ⬅️ Flag pour afficher la preview uniquement après interaction slider

  private tfOpen = false;
  private kbOpen = false;

  private musicOpen = false;
  private musicEnabled = false;
  private selectedTrack: string | null = null;
  private isPlaying = false;
  private currentTime = 0;
  private duration = 0;
  private volume = 0.8;

  private audioCtx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private sourceNode: MediaElementAudioSourceNode | null = null;
  private silentGain: GainNode | null = null;
  private freqData: Uint8Array | null = null;

  private spectrumRAF = 0;
  private spectrumRo: ResizeObserver | null = null;

  // 🔹 Index courant dans la playlist
  private currentTrackIndex = 0;

  private musicSearch = '';

  // ====== TRIM MUSIQUE ======
  // en secondes
  private trimStart = 0;
  private trimEnd = 0;
  private draggingTrim: 'start' | 'end' | null = null;
  private minTrimGap = 0.25; // 250ms min entre début et fin

  private hoverTrim: 'start' | 'end' | null = null;

  // (déjà existant dans ton code, on le garde)
  private seekingOnCanvas = false;

  private musicOptions: { value: string; text: string; genres: string[] }[] = [];
  private selectedGenres: string[] = [];

  // --- Waveform statique (pas d'animation) ---
  private spectrumMode: 'bars' | 'waveform' = 'waveform'; // mets 'waveform' pour activer ce rendu par défaut
  private waveformMin: Float32Array | null = null; // pics négatifs pré-calculés
  private waveformMax: Float32Array | null = null; // pics positifs pré-calculés
  private waveformDur = 0; // durée du buffer utilisé pour la WF
  private waveformBuiltForUrl: string | null = null; // pour éviter de recalculer si la même URL
  private waveformHiResCols = 2048; // résolution interne (recommandé: 1024–4096)

  private musicLibLoading = false;
  private musicLibError: string | null = null;

  private setCanvasCursor(cursor: string) {
    const c = this.$refs.spectrumCanvas as HTMLCanvasElement | undefined;
    if (c) c.style.cursor = cursor;
  }

  private getSelectionBoundsPx(canvas: HTMLCanvasElement) {
    const rect = canvas.getBoundingClientRect();
    const dur = Math.max(this.duration || 0, 0.0001);
    return {
      xStart: (this.trimStart / dur) * rect.width,
      xEnd: (this.trimEnd / dur) * rect.width,
      rect,
    };
  }

  private clearWaveform(): void {
    this.waveformMin = null;
    this.waveformMax = null;
    this.waveformDur = 0;
    this.waveformBuiltForUrl = null;
  }

  private async buildWaveform(): Promise<void> {
    if (!this.musicOpen || !this.musicEnabled) {
      this.clearWaveform();
      return;
    }
    if (!this.selectedTrack) {
      this.clearWaveform();
      return;
    }
    if (this.waveformBuiltForUrl === this.selectedTrack && this.waveformMin && this.waveformMax) return;

    try {
      const resp = await fetch(this.selectedTrack, { mode: 'cors', cache: 'force-cache' });
      const ab = await resp.arrayBuffer();

      const ctx = this.getAudioCtx();
      const audioBuf = await ctx.decodeAudioData(ab);

      const numCh = audioBuf.numberOfChannels;
      const len = audioBuf.length;
      const dur = audioBuf.duration;

      // mixdown mono
      const mono = new Float32Array(len);
      for (let ch = 0; ch < numCh; ch++) {
        const data = audioBuf.getChannelData(ch);
        for (let i = 0; i < len; i++) mono[i] += data[i];
      }
      const inv = 1 / numCh;
      for (let i = 0; i < len; i++) mono[i] *= inv;

      // calcule pics min/max par "bloc" (downsampling en pics)
      const cols = this.waveformHiResCols;
      const block = Math.max(1, Math.floor(len / cols));
      const mins = new Float32Array(cols);
      const maxs = new Float32Array(cols);

      for (let c = 0; c < cols; c++) {
        const start = c * block;
        const end = c === cols - 1 ? len : Math.min(len, start + block);
        let minV = 1.0;
        let maxV = -1.0;
        for (let i = start; i < end; i++) {
          const v = mono[i];
          if (v < minV) minV = v;
          if (v > maxV) maxV = v;
        }
        mins[c] = minV;
        maxs[c] = maxV;
      }

      this.waveformMin = mins;
      this.waveformMax = maxs;
      this.waveformDur = dur;
      this.waveformBuiltForUrl = this.selectedTrack;

      // redraw une fois prête
      this.drawSpectrum(false);
    } catch (e) {
      console.error('buildWaveform failed:', e);
      this.clearWaveform();
    }
  }

  private isInsideSelection(px: number, canvas: HTMLCanvasElement) {
    const { xStart, xEnd } = this.getSelectionBoundsPx(canvas);
    return px >= xStart && px <= xEnd;
  }

  get uniqueGenres(): string[] {
    const s = new Set<string>();
    for (const o of this.musicOptions) {
      (o.genres || []).forEach((g) => s.add(g));
    }
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }

  get isMusicPanelBusy(): boolean {
    return this.musicLibLoading || this.musicOptions.length === 0;
  }

  get filteredMusic() {
    const q = this.musicSearch.trim().toLowerCase();

    let list = this.musicOptions;

    if (q) {
      list = list.filter((o) => (o.text || '').toLowerCase().includes(q));
    }

    if (this.selectedGenres.length > 0) {
      list = list.filter((o) => (o.genres || []).some((g) => this.selectedGenres.includes(g)));
    }

    return list;
  }

  // 🔹 Vérifie si on peut naviguer
  get canGoPrev(): boolean {
    return this.musicOptions.length > 0 && this.currentTrackIndex > 0;
  }

  get canGoNext(): boolean {
    return this.musicOptions.length > 0 && this.currentTrackIndex < this.musicOptions.length - 1;
  }

  // Récupère le titre du morceau sélectionné
  get currentTrackTitle(): string {
    const found = this.musicOptions.find((opt) => opt.value === this.selectedTrack);
    return found ? found.text : '';
  }

  get fadeInSeconds(): number {
    const ms = (this.ensureStab() as any).musicFadeInMs || 0;
    return ms / 1000;
  }
  set fadeInSeconds(seconds: number) {
    const s = this.ensureStab() as any;
    s.musicFadeInMs = Math.round(seconds * 1000);
  }

  get fadeInDisplay(): string {
    const s = this.fadeInSeconds;
    return s === 0 ? '0s' : s.toFixed(2) + 's';
  }

  // Computed pour Fondu sortie (affichage + conversion)
  get fadeOutSeconds(): number {
    const ms = (this.ensureStab() as any).musicFadeOutMs || 0;
    return ms / 1000;
  }
  set fadeOutSeconds(seconds: number) {
    const s = this.ensureStab() as any;
    s.musicFadeOutMs = Math.round(seconds * 1000);
  }

  get fadeOutDisplay(): string {
    const s = this.fadeOutSeconds;
    return s === 0 ? '0s' : s.toFixed(2) + 's';
  }

  // Computed pour Gain (affichage uniquement)
  get gainDisplay(): string {
    const db = (this.ensureStab() as any).musicGainDb || 0;
    return db >= 0 ? `+${db.toFixed(1)} dB` : `${db.toFixed(1)} dB`;
  }

  private readonly TF_DEFAULTS: Partial<TemporalFilterConfig> = {
    enabled: false,
    mode: TemporalFilterMode.tmix,
    tpad: true,
    tpadFrames: 1,
    tmixFrames: 3,
    tmixWeights: '1 1 1',
    tmixPlanes: 1,
  };

  // getter/setter du toggle
  get tfEnabled(): boolean {
    return !!this.ensureTF().enabled;
  }
  set tfEnabled(v: boolean) {
    const tf = this.ensureTF();
    tf.enabled = !!v;
    if (v) this.seedTFKeys(false);
  }

  get tmixFrameOptions() {
    return [
      { value: 3, text: '3' },
      { value: 5, text: '5' },
      { value: 7, text: '7' },
      { value: 9, text: '9' },
    ];
  }

  private tmixPreset: 'uniform' | 'binomial' | 'front' | 'back' = 'uniform';

  // 3) Getter/Setter frames avec side-effect (recalcule les poids)
  get tmixFrames(): number {
    const tf = this.ensureTF();
    return tf.tmixFrames ?? 3;
  }
  set tmixFrames(v: number) {
    const tf = this.ensureTF();
    tf.tmixFrames = v;
    // Re-applique le preset courant pour régénérer la bonne séquence
    this.applyTmixPreset();
  }

  // 4) Options de presets dépendantes de la fenêtre
  get tmixWeightOptions() {
    const n = this.tmixFrames;
    const uni = this.seqUniform(n).join(' ');
    const bino = this.seqBinomial(n).join(' ');
    const front = this.seqRamp(n, 'asc').join(' ');
    const back = this.seqRamp(n, 'desc').join(' ');

    return [
      { value: 'uniform', text: `Uniforme (${uni})` },
      { value: 'binomial', text: `Binomial (${bino})` },
      { value: 'front', text: `Avant fort (${front})` },
      { value: 'back', text: `Arrière fort (${back})` },
    ];
  }

  onKBChanged() {
    // hook dispo
  }

  // 5) Applique le preset au champ tmixWeights
  private applyTmixPreset() {
    const tf = this.ensureTF();
    const n = this.tmixFrames;

    let seq: number[];
    switch (this.tmixPreset) {
      case 'binomial':
        seq = this.seqBinomial(n);
        break;
      case 'front':
        seq = this.seqRamp(n, 'asc');
        break;
      case 'back':
        seq = this.seqRamp(n, 'desc');
        break;
      case 'uniform':
      default:
        seq = this.seqUniform(n);
    }
    tf.tmixWeights = seq.join(' ');
  }

  // 6) Générateurs de séquences
  private seqUniform(n: number): number[] {
    return Array.from({ length: n }, () => 1);
  }

  private seqRamp(n: number, dir: 'asc' | 'desc'): number[] {
    const asc = Array.from({ length: n }, (_v, i) => i + 1);
    return dir === 'asc' ? asc : asc.slice().reverse();
  }

  private seqBinomial(n: number): number[] {
    const row: number[] = [1];
    for (let k = 0; k < n - 1; k++) {
      const next: number[] = [1];
      for (let i = 0; i < row.length - 1; i++) next.push(row[i] + row[i + 1]);
      next.push(1);
      row.splice(0, row.length, ...next);
    }
    return row;
  }

  /* ===== Hooks utiles ===== */

  // Appelé quand on active le bloc TF
  public onToggleTF(val: boolean): void {
    this.tfEnabled = !!val;
    if (val && this.ensureTF().mode === 'tmix') {
      if (!this.ensureTF().tmixFrames) this.tmixFrames = 3;
      this.applyTmixPreset();
    }
  }

  // assure l’existence du bloc temporal
  private ensureTF(): TemporalFilterConfig {
    const s = this.ensureStab();
    const current = (s as any).temporal;

    // 1) Si c'est déjà une instance, on ne touche à rien
    if (current instanceof TemporalFilterConfig) {
      return current;
    }

    // 2) Si on a un objet brut venant du backend, on le copie dans une vraie instance
    if (current && typeof current === 'object') {
      const init = Object.assign(new TemporalFilterConfig(), current);
      this.$set(s as any, 'temporal', init);
      return init;
    }

    // 3) Sinon on crée une nouvelle instance avec les defaults
    const init = new TemporalFilterConfig();
    init.enabled = false;
    init.mode = TemporalFilterMode.tmix;
    this.$set(s as any, 'temporal', init);
    return init;
  }

  // injecte les valeurs par défaut
  private seedTFKeys(overwrite: boolean) {
    const tf = this.ensureTF();
    Object.entries(this.TF_DEFAULTS).forEach(([k, v]) => {
      const key = k as keyof TemporalFilterConfig;
      if (key === 'enabled') return;
      if (overwrite || typeof (tf as any)[key] === 'undefined') {
        // @ts-ignore
        this.$set(tf as any, key as any, v as any);
      }
    });
  }

  private onTFModeChanged() {
    const tf = this.ensureTF();
    if (tf.mode === 'tmix') {
      if (!tf.tmixFrames) this.tmixFrames = 3;
      this.tmixPreset = 'uniform';
      this.applyTmixPreset();
    }
  }

  public resetTFToDefaults(): void {
    const tf = this.ensureTF();
    if (!tf.enabled) return;
    this.seedTFKeys(true);
  }

  public onResetTF(): void {
    const tf = this.ensureTF();
    if (!tf.enabled) return;
    const wasEnabled = tf.enabled;
    this.seedTFKeys(true);
    tf.enabled = wasEnabled;
    this.$forceUpdate();
  }

  // v-model du parent
  private get model() {
    return this.value;
  }
  private set model(val: TimelapseConfiguration) {
    const compare = JSON.stringify(val).localeCompare(JSON.stringify(this.value));
    if (compare !== 0) {
      this.$emit('input', val);
    }
  }

  get stabEnabled(): boolean {
    return !!this.ensureStab().enabled;
  }
  set stabEnabled(val: boolean) {
    const s = this.ensureStab();
    s.enabled = !!val;
  }

  get methodIsSift(): boolean {
    return !!this.ensureStab().useSiftIfAvailable;
  }
  set methodIsSift(v: boolean) {
    this.ensureStab().useSiftIfAvailable = !!v;
  }

  get topIgnorePercent(): number {
    const r = this.ensureStab().topIgnoreRatio || 0;
    return Math.round(r * 100);
  }
  set topIgnorePercent(v: number) {
    const clamped = Math.max(0, Math.min(100, v));
    this.ensureStab().topIgnoreRatio = clamped / 100;
  }

  // Rotation éventuelle de l’aperçu selon la caméra
  get previewTransform() {
    const deg = this.camera?.rotate || 0;
    return deg ? { transform: `rotate(${deg}deg)` } : {};
  }

  // Valeurs d'origine pour reset des sliders overlay
  private originalmodel: ImageModifier = {
    iso: 0,
    brightness: 0,
    contrast: 0,
    saturation: 0,
    white: 0,
    crop: Crop.original,
    watermarkPosition: Position.bottomleft,
    erigeWatermark: true,
    customWatermarkPicture: undefined,
  };

  private readonly STAB_DEFAULTS: Partial<StabilizationConfig> = {
    topIgnoreRatio: 0.25,
    useSiftIfAvailable: true,
    nFeaturesSift: 10000,
    ratioTest: 0.7,
    minMatchesForH: 100,
    minMatchesForAffine: 50,
    ransacReproj: 1.5,
    ransacConf: 0.9995,
    ransacIters: 8000,
    percentile: 97,
    analyzeDownscale: 0.8,
    resizeOutputMaxW: 0,
    useCapFfmpeg: true,

    claheEnabled: false,
    claheClip: 1.3,
    claheGrid: 12,
    claheBlend: 0.5,
  };

  private seedStabKeys(overwrite: boolean) {
    const s = this.ensureStab();

    const defaults: any = {
      // --- Stab “classique” ---
      topIgnoreRatio: 0.25,
      useSiftIfAvailable: true,
      nFeaturesSift: 10000,
      ratioTest: 0.7,
      minMatchesForH: 100,
      minMatchesForAffine: 50,
      ransacReproj: 1.5,
      ransacConf: 0.9995,
      ransacIters: 8000,
      percentile: 97,
      analyzeDownscale: 0.8,
      resizeOutputMaxW: 0,
      useCapFfmpeg: true,

      claheEnabled: false,
      claheClip: 1.3,
      claheGrid: 12,
      claheBlend: 0.5,

      // --- Ken Burns “à plat” ---
      kenburnsEnabled: false,
      kbEasing: 'linear',
      kbStartX: 0,
      kbStartY: 0,
      kbStartW: 1,
      kbStartH: 1,
      kbEndX: 0.1,
      kbEndY: 0.1,
      kbEndW: 0.8,
      kbEndH: 0.8,
    };

    Object.entries(defaults).forEach(([k, v]) => {
      if (overwrite || typeof (s as any)[k] === 'undefined') {
        this.$set(s as any, k, v);
      }
    });
  }

  private datas: number[] = [...Array(101).keys()].map((x) => x - 50);

  /* =========================
   *       LIFECYCLE
   * =======================*/
  public created(): void {
    const s = this.ensureStab();
    if (typeof s.enabled === 'undefined') s.enabled = false;

    if (s.enabled) this.seedStabKeys(false);
  }

  public mounted(): void {
    logger.info('mounted');
    this.loadPreview();
    this.$nextTick(() => {
      const canvas = this.$refs.spectrumCanvas as HTMLCanvasElement;
      if (!canvas) return;
      this.spectrumRo = new ResizeObserver(() => {
        this.syncSpectrumSize();
        this.drawSpectrum(false);
      });
      this.spectrumRo.observe(canvas);
    });
    this.syncMusicFromModel();
  }

  public beforeDestroy() {
    if (this.spectrumRo) {
      this.spectrumRo.disconnect();
      this.spectrumRo = null;
    }
    cancelAnimationFrame(this.spectrumRAF);
    try {
      this.sourceNode?.disconnect();
    } catch {
      // nothing
    }
    try {
      this.analyser?.disconnect();
    } catch {
      // nothing
    }
    try {
      this.silentGain?.disconnect();
    } catch {
      // nothing
    }
    this.sourceNode = null;
    this.analyser = null;
    this.silentGain = null;
  }

  private smallToMediumKey(key?: string): string {
    if (!key) return '';

    return key;
  }

  private async loadPreview() {
    this.previewSrc = '';

    const keyRaw = this.camera?.lastPhoto?.key;
    if (!keyRaw) {
      console.log('[preview] aucune clé trouvée');
      return;
    }

    try {
      const keyMedium = this.smallToMediumKey(keyRaw);
      const keyToUse = keyMedium || keyRaw;

      const normalizedKey = keyToUse.startsWith('public/') ? keyToUse : `${keyToUse}`;

      const url = await Storage.get(normalizedKey, { level: 'public', expires: 3600 });

      this.previewSrc = String(url);
      console.log('[preview] ✅ URL signée chargée :', this.previewSrc);
    } catch (error) {
      console.log('[preview] ❌ Échec Storage.get :', error);
      this.previewSrc = '';
    }
  }

  /* =========================
   *         UI GETTERS
   * =======================*/
  // Options du select "interpol"
  get interpolOptions() {
    return [
      { value: StabilizationInterpol.bilinear, text: 'bilinear' },
      { value: StabilizationInterpol.bicubic, text: 'bicubic' },
      { value: StabilizationInterpol.lanczos, text: 'lanczos' },
    ];
  }

  // Options du select "crop"
  get cropOptions() {
    return [
      { value: StabilizationCrop.keep, text: this.$t('timelapseedition.crop_keep') as string },
      { value: StabilizationCrop.fixed, text: this.$t('timelapseedition.crop_fixed') as string },
      { value: StabilizationCrop.black, text: this.$t('timelapseedition.crop_black') as string },
      { value: StabilizationCrop.none, text: this.$t('timelapseedition.crop_none') as string },
      { value: StabilizationCrop.expand, text: this.$t('timelapseedition.crop_expand') as string },
    ];
  }

  /* =========================
   *     FONCTIONS OVERLAY
   * =======================*/
  public process = (dotsPos: number[]): number[][] => [[50, dotsPos[0]]];

  public getCustomWatermark(key: string): string {
    if (/data:(image\/.+);base64,/.test(key)) {
      return key;
    } else {
      return `https://${process.env.VUE_APP_MAIN_STORE_BUCKET}.s3.${process.env.VUE_APP_MAIN_STORE_REGION}.amazonaws.com/public/${key}`;
    }
  }

  public removeCustomWatermarkPicture(): void {
    this.model.customWatermarkPicture = undefined;
  }

  public changeActiveModifier($event: Event): void {
    // @ts-ignore dom event
    this.activeModifier = $event.currentTarget.id;
  }

  public onUpload($event: HTMLInputEvent): void {
    logger.info('onUpload', $event);
    let file: File;
    if ($event && $event.target && ($event.target as any).files) {
      file = ($event.target as any).files[0];
      photoService
        .fileToBase64(file)
        .then((base64: string) => {
          Vue.set(this.model, 'customWatermarkPicture', base64);
          this.validLogo = true;
        })
        .catch((error) => {
          if (error.message && error.message === 'INVALID_SIZE') {
            this.model.customWatermarkPicture = undefined;
            this.validLogo = false;
          } else {
            throw error;
          }
        });
    }
  }

  public resetSlider(): void {
    if (this.activeModifier !== null) {
      // @ts-ignore it's ok
      this.model[this.activeModifier] = this.originalmodel[this.activeModifier];
    }
  }

  /* =========================
   *   FONCTIONS STABILISATION
   * =======================*/
  public onToggleStab(val: boolean): void {
    const s = this.ensureStab();
    s.enabled = !!val;

    if (val) {
      // Activation : on injecte les valeurs par défaut si besoin
      this.seedStabKeys(false);
    } else {
      // Désactivation : on replie l’accordéon avancé
      this.stabMoreOpen = false;
    }
  }

  public resetStabToDefaults(): void {
    const s = this.ensureStab();
    if (!s.enabled) return;
    this.seedStabKeys(true);
  }

  private ensureStab(): StabilizationConfig {
    const s = (this.model as any).stabilization as StabilizationConfig | undefined;
    if (s) return s;

    const init = Object.assign(new StabilizationConfig(), { enabled: false });
    this.$set(this.model as any, 'stabilization', init);
    return init;
  }

  private toggleStabHeader(): void {
    this.stabOpen = !this.stabOpen;
  }

  // Événements pour le slider : afficher la preview au début du drag
  private onSliderDragStart() {
    if (this.previewSrc) {
      this.showPreview = true;
    }
  }

  private onSliderDragEnd() {
    if (this.previewSrc) {
      this.showPreview = false;
    }
  }

  // ====== AUDIO / MUSIQUE ======

  // Active / désactive la musique
  public async onToggleMusic(): Promise<void> {
    const s = this.ensureStab();
    (s as any).musicEnabled = this.musicEnabled; // ✅ persiste ON/OFF

    const audio = this.$refs.audioPlayer as HTMLAudioElement | undefined;

    if (!this.musicEnabled || !this.musicOpen) {
      audio?.pause();
      this.isPlaying = false;
      this.currentTime = 0;
      this.selectedTrack = null;

      // Nettoyage graphe/analyser
      this.teardownAudioGraph();
      this.spectrumRo?.disconnect();

      return;
    }

    // 🎛️ À l’activation, garantir l’existence des valeurs par défaut
    this.seedMusicKeys(false);

    // Charger la lib si nécessaire
    if (!this.musicOptions.length) await this.fetchMusicLibrary();

    this.$nextTick(() => {
      const a = this.$refs.audioPlayer as HTMLAudioElement | undefined;
      a?.load();
      if (a) {
        a.autoplay = false;
        a.pause();
        a.currentTime = 0;
      }
      this.ensureAudioGraph();

      const canvas = this.$refs.spectrumCanvas as HTMLCanvasElement | undefined;
      if (canvas) {
        if (!this.spectrumRo) {
          this.spectrumRo = new ResizeObserver(() => {
            this.syncSpectrumSize();
            this.drawSpectrum(false);
          });
        } else {
          this.spectrumRo.disconnect();
        }
        this.spectrumRo.observe(canvas);
        this.syncSpectrumSize();
        this.drawSpectrum(false);
      }
    });
  }

  private onAudioError = (e: Event) => {
    const a = e.target as HTMLAudioElement;
    console.error('Audio error', a?.error);
  };

  private getAudioCtx(): AudioContext {
    if (!this.musicOpen) return this.audioCtx as any; // garde passive
    if (!this.audioCtx) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      this.audioCtx = new Ctx();
    }
    return this.audioCtx;
  }

  private async fetchMusicLibrary() {
    if (this.musicLibLoading) return; // Évite les doubles appels
    this.musicLibLoading = true;
    this.musicLibError = null;
    try {
      const response = await fetch('https://ayg1dycdug.execute-api.eu-west-3.amazonaws.com/prod/music-library');
      if (!response.ok) throw new Error('Erreur API');
      const data = await response.json();
      this.musicOptions = data.map((track: any) => ({
        value: track.url,
        text: String(track.title || ''),
        genres: Array.isArray(track.genre) ? track.genre : [],
      }));
    } catch (err) {
      console.error('Erreur chargement bibliothèque musique:', err);
      this.musicLibError = 'Impossible de charger la bibliothèque musicale. Réessayez.';
      this.musicOptions = []; // Reset sur erreur
    } finally {
      this.musicLibLoading = false;
    }
  }

  public updateProgress(): void {
    const audio = this.$refs.audioPlayer as HTMLAudioElement;
    if (audio && !isNaN(audio.currentTime)) {
      // Clamp dans la plage sélectionnée
      const t = audio.currentTime;
      const clamped = Math.min(Math.max(t, this.trimStart), this.trimEnd);
      this.currentTime = clamped;

      // Si on atteint la fin de la plage
      if (t >= this.trimEnd - 0.01) {
        if ((this.ensureStab() as any).musicLoop) {
          audio.currentTime = this.trimStart;
          audio.play().catch(() => {
            // ignore
          });
        } else {
          audio.pause();
          this.isPlaying = false;
        }
      }

      if (!this.isPlaying) this.drawSpectrum(false);
    }
  }

  public onLoadedMetadata(): void {
    if (!this.musicOpen) return;
    const audio = this.$refs.audioPlayer as HTMLAudioElement | undefined;
    if (!audio) return;
    audio.muted = false;
    audio.volume = this.volume;
    this.duration = audio.duration;
    this.ensureAudioGraph();
    this.buildWaveform();
    this.drawSpectrum(false);

    // 🔹 Init plage si nécessaire
    const s = this.ensureStab() as any;

    if (!s.musicStartMs || s.musicStartMs < 0) s.musicStartMs = 0;
    if (!s.musicEndMs || s.musicEndMs <= 0 || s.musicEndMs > Math.round(this.duration * 1000)) {
      s.musicEndMs = Math.round(this.duration * 1000);
    }

    this.trimStart = (s.musicStartMs || 0) / 1000;
    this.trimEnd = (s.musicEndMs || Math.round(this.duration * 1000)) / 1000;

    // clamps
    this.trimStart = Math.max(0, Math.min(this.trimStart, Math.max(0, this.duration - this.minTrimGap)));
    this.trimEnd = Math.max(this.trimStart + this.minTrimGap, Math.min(this.trimEnd, this.duration));

    // recale le curseur si hors plage
    if (this.currentTime < this.trimStart || this.currentTime > this.trimEnd) {
      this.currentTime = this.trimStart;
      if (audio) audio.currentTime = this.trimStart;
    }
  }

  public seekAudio(): void {
    const audio = this.$refs.audioPlayer as HTMLAudioElement;
    if (audio) audio.currentTime = this.currentTime;
  }

  public setVolume(): void {
    const audio = this.$refs.audioPlayer as HTMLAudioElement;
    if (audio) audio.volume = this.volume;
  }

  public onEnded(): void {
    this.isPlaying = false;
    this.currentTime = this.trimStart; // revenir au début de la plage
    this.stopSpectrum();
  }

  public formatTime(seconds: number): string {
    if (isNaN(seconds)) return '0:00';
    const min = Math.floor(seconds / 60);
    const sec = Math.floor(seconds % 60)
      .toString()
      .padStart(2, '0');
    return `${min}:${sec}`;
  }

  // 🔹 Passe au morceau suivant
  public nextTrack(): void {
    if (!this.canGoNext) return;
    this.currentTrackIndex++;
    this.changeTrackByIndex();
  }

  // 🔹 Revient au morceau précédent
  public previousTrack(): void {
    if (!this.canGoPrev) return;
    this.currentTrackIndex--;
    this.changeTrackByIndex();
  }

  // 🔹 Met à jour la musique courante et relance la lecture
  private changeTrackByIndex(): void {
    if (!this.musicOpen) return;
    const track = this.musicOptions[this.currentTrackIndex];
    if (!track) return;

    const wasPlaying = this.isPlaying;

    this.selectedTrack = track.value;
    this.isPlaying = false;
    this.currentTime = 0;

    const s = this.ensureStab() as any;
    s.musicUrl = this.selectedTrack;
    s.musicTitle = track.text || '';
    s.musicStartMs = 0;
    s.musicEndMs = 0;

    this.clearWaveform();
    this.$nextTick(() => {
      const a = this.$refs.audioPlayer as HTMLAudioElement | undefined;
      a?.load();
      if (a) {
        a.autoplay = false;
        a.pause();
      }
      if (wasPlaying) this.togglePreview();
      // on relit seulement si on lisait déjà
      else this.drawSpectrum(false);
    });
  }

  // 🔹 Quand on change manuellement depuis le sélecteur/cartes
  public onMusicSelected(): void {
    if (!this.musicOpen) return;
    const audio = this.$refs.audioPlayer as HTMLAudioElement;
    if (audio) audio.pause();
    const idx = this.musicOptions.findIndex((opt) => opt.value === this.selectedTrack);
    this.currentTrackIndex = idx >= 0 ? idx : 0;
    this.isPlaying = false;
    this.currentTime = 0;

    const s = this.ensureStab();
    s.musicUrl = this.selectedTrack || undefined;
    s.musicTitle = this.currentTrackTitle || undefined;

    // (re)construire la WF pour la nouvelle piste
    this.clearWaveform();
    this.$nextTick(() => {
      const a = this.$refs.audioPlayer as HTMLAudioElement | undefined;
      a?.load(); // déclenchera onLoadedMetadata ⇒ buildWaveform()
    });
  }

  private ensureAudioGraph(): void {
    if (!this.musicOpen || !this.musicEnabled) return;
    const audio = this.$refs.audioPlayer as HTMLAudioElement | undefined;
    if (!audio) return;

    const ctx = this.getAudioCtx();

    const source = this.sourceNode ?? (this.sourceNode = ctx.createMediaElementSource(audio));
    const analyser = this.analyser ?? (this.analyser = ctx.createAnalyser());
    const gain = this.silentGain ?? (this.silentGain = ctx.createGain());

    analyser.fftSize = 2048;
    analyser.smoothingTimeConstant = 0.7;
    gain.gain.value = 1;

    try {
      source.disconnect();
    } catch {
      // ignore
    }
    try {
      analyser.disconnect();
    } catch {
      // ignore
    }
    try {
      gain.disconnect();
    } catch {
      // ignore
    }

    source.connect(analyser);
    analyser.connect(gain);
    gain.connect(ctx.destination);

    this.freqData = new Uint8Array(analyser.frequencyBinCount);
  }

  private startSpectrum(): void {
    if (!this.musicOpen || !this.musicEnabled) return;
    cancelAnimationFrame(this.spectrumRAF);
    const loop = () => {
      this.drawSpectrum(true);
      this.spectrumRAF = requestAnimationFrame(loop);
    };
    this.spectrumRAF = requestAnimationFrame(loop);
  }

  private stopSpectrum(): void {
    cancelAnimationFrame(this.spectrumRAF);
    this.spectrumRAF = 0;
    this.drawSpectrum(false);
  }

  private syncSpectrumSize(): void {
    if (!this.musicOpen) return;
    const canvas = this.$refs.spectrumCanvas as HTMLCanvasElement;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.max(1, Math.floor(rect.width * dpr));
    canvas.height = Math.max(1, Math.floor(rect.height * dpr));
  }

  private drawSpectrum(live: boolean): void {
    if (!this.musicOpen || !this.musicEnabled) return;
    const canvas = this.$refs.spectrumCanvas as HTMLCanvasElement;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const W = canvas.width;
    const H = canvas.height;

    // fond doux
    ctx.clearRect(0, 0, W, H);
    const bg = ctx.createLinearGradient(0, 0, 0, H);
    bg.addColorStop(0, 'rgba(255,255,255,0.04)');
    bg.addColorStop(1, 'rgba(0,0,0,0.10)');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    const hasAnalyser = !!this.analyser;

    /* =========================
     * MODE "bars" (FFT en live)
     * =======================*/
    if (hasAnalyser && this.spectrumMode === 'bars') {
      if (!this.freqData || this.freqData.length !== this.analyser!.frequencyBinCount) {
        this.freqData = new Uint8Array(this.analyser!.frequencyBinCount);
      }
      if (live) this.analyser!.getByteFrequencyData(this.freqData);

      // ligne de base
      ctx.globalAlpha = 0.35;
      ctx.fillStyle = '#5fc9e4';
      ctx.fillRect(0, Math.floor(H * 0.66), W, Math.max(1, Math.floor(1 * dpr)));
      ctx.globalAlpha = 1;

      const ctxAudio = this.getAudioCtx();
      const nyquist = ctxAudio.sampleRate / 2;
      const fMin = 40,
        fMax = 16000;
      const logMin = Math.log10(fMin),
        logMax = Math.log10(fMax);

      const N = this.freqData.length;
      const bars = Math.min(96, Math.floor(W / (6 * dpr)));

      for (let i = 0; i < bars; i++) {
        const frac0 = i / bars,
          frac1 = (i + 1) / bars;
        const f0 = 10 ** (logMin + frac0 * (logMax - logMin));
        const f1 = 10 ** (logMin + frac1 * (logMax - logMin));
        const b0 = Math.max(1, Math.floor((f0 / nyquist) * N));
        const b1 = Math.min(N - 1, Math.ceil((f1 / nyquist) * N));

        let sum = 0;
        for (let k = b0; k <= b1; k++) sum += this.freqData[k];
        const v = sum / Math.max(1, b1 - b0 + 1); // 0..255

        const tilt = Math.pow(i / bars, 0.35);
        const amp = Math.min(1, (v / 255) * (0.7 + 0.8 * tilt));

        const barW = Math.max(2 * dpr, Math.floor(W / bars) - 2 * dpr);
        const x = Math.floor(i * (W / bars));
        const barH = Math.max(2 * dpr, Math.floor(amp * (H * 0.9)));

        const grad = ctx.createLinearGradient(0, H - barH, 0, H);
        grad.addColorStop(0, '#7fe3ff');
        grad.addColorStop(1, '#5fc9e4');
        ctx.fillStyle = grad;
        ctx.fillRect(x, H - barH, barW, barH);
      }
    }

    /* ==========================================
     * MODE "waveform" (forme d’onde — STATIQUE)
     * ========================================*/
    if (this.spectrumMode === 'waveform') {
      const baselineY = Math.floor(H / 2);

      // ligne centrale
      ctx.globalAlpha = 0.3;
      ctx.fillStyle = '#5fc9e4';
      ctx.fillRect(0, baselineY, W, Math.max(1, Math.floor(1 * dpr)));
      ctx.globalAlpha = 1;

      // si on a les pics = on dessine la WF statique, sinon un placeholder
      const haveWF = !!(this.waveformMin && this.waveformMax && this.waveformMin.length === this.waveformMax.length);
      const columns = Math.min(1024, Math.max(120, Math.floor(W / (2 * dpr))));
      const barW = Math.max(1 * dpr, Math.floor(W / columns) - Math.floor(0.5 * dpr));
      const maxHalfH = H * 0.48;

      ctx.save();
      ctx.shadowColor = 'rgba(127,227,255,0.45)';
      ctx.shadowBlur = 8 * dpr;
      const grad = ctx.createLinearGradient(0, baselineY - maxHalfH, 0, baselineY + maxHalfH);
      grad.addColorStop(0, '#7fe3ff');
      grad.addColorStop(0.5, '#c6f3ff');
      grad.addColorStop(1, '#5fc9e4');
      ctx.fillStyle = grad;

      if (haveWF) {
        const srcLen = this.waveformMax!.length;
        for (let i = 0; i < columns; i++) {
          // échantillonnage des pics hi-res → colonne écran
          const idx = Math.min(srcLen - 1, Math.floor((i / (columns - 1)) * (srcLen - 1)));
          const minV = this.waveformMin![idx]; // -1..1
          const maxV = this.waveformMax![idx]; // -1..1
          const amp = Math.max(Math.abs(minV), Math.abs(maxV)); // 0..1

          // léger easing pour un rendu moins "haché"
          const eased = Math.pow(amp, 0.85);
          const halfH = Math.max(1 * dpr, Math.min(maxHalfH, eased * maxHalfH));

          const xCenter = Math.floor(i * (W / columns) + W / columns / 2);
          const yTop = baselineY - halfH;
          const h = 2 * halfH;
          const r = Math.min(halfH, Math.max(1 * dpr, barW * 0.4));
          const x = xCenter - Math.floor(barW / 2);

          ctx.beginPath();
          if (r > 0) {
            ctx.moveTo(x + r, yTop);
            ctx.arcTo(x + barW, yTop, x + barW, yTop + r, r);
            ctx.arcTo(x + barW, yTop + h, x + barW - r, yTop + h, r);
            ctx.arcTo(x, yTop + h, x, yTop + h - r, r);
            ctx.arcTo(x, yTop, x + r, yTop, r);
          } else {
            ctx.rect(x, yTop, barW, h);
          }
          ctx.fill();
        }
      } else {
        // placeholder discret (en attendant le precalc)
        ctx.globalAlpha = 0.25;
        ctx.fillRect(0, baselineY - Math.floor(6 * dpr), W, Math.floor(12 * dpr));
        ctx.globalAlpha = 1;
      }
      ctx.restore();
    }

    /* =========================
     *   OVERLAYS TRIM/CURSOR
     * =======================*/
    const hasDur = this.duration > 0;
    const trimStart = (this as any).trimStart ?? 0;
    const trimEnd = (this as any).trimEnd ?? (hasDur ? this.duration : 0);
    const xStart = hasDur ? Math.floor((trimStart / this.duration) * W) : 0;
    const xEnd = hasDur ? Math.floor((trimEnd / this.duration) * W) : W;

    // zones hors plage
    ctx.fillStyle = 'rgba(0,0,0,0.35)';
    ctx.fillRect(0, 0, xStart, H);
    ctx.fillRect(xEnd, 0, W - xEnd, H);

    // plage retenue
    ctx.fillStyle = 'rgba(95,201,228,0.12)';
    ctx.fillRect(xStart, 0, Math.max(0, xEnd - xStart), H);

    // helpers/poignées
    const handleBarW = Math.max(2, Math.floor(2 * dpr));
    const knobR = Math.round(6 * dpr);
    const knobRActive = Math.round(8 * dpr);

    const drawBubble = (x: number, label: string) => {
      const padX = 6 * dpr;
      const by = Math.round(1 * dpr);
      const bh = Math.round(18 * dpr);

      ctx.save();
      ctx.font = `${Math.round(12 * dpr)}px system-ui, -apple-system, Segoe UI, Roboto, sans-serif`;
      const tw = ctx.measureText(label).width;
      const bw = tw + padX * 2;

      let bx = Math.round(x - bw / 2);
      bx = Math.max(2 * dpr, Math.min(bx, W - bw - 2 * dpr));
      const r = 6 * dpr;

      ctx.beginPath();
      ctx.moveTo(bx + r, by);
      ctx.arcTo(bx + bw, by, bx + bw, by + bh, r);
      ctx.arcTo(bx + bw, by + bh, bx, by + bh, r);
      ctx.arcTo(bx, by + bh, bx, by, r);
      ctx.arcTo(bx, by, bx + r, by, r);
      ctx.closePath();

      ctx.fillStyle = 'rgba(3,28,57,0.95)';
      ctx.strokeStyle = 'rgba(95,201,228,0.85)';
      ctx.lineWidth = 1 * dpr;
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = '#e6faff';
      ctx.fillText(label, bx + padX, by + bh - 6 * dpr);
      ctx.restore();
    };

    const drawHandle = (x: number, active: boolean, timeSec: number) => {
      ctx.fillStyle = active ? '#4dffcd' : '#4dffcd';
      ctx.fillRect(x - Math.floor(handleBarW / 2), 0, handleBarW, H);

      ctx.save();
      ctx.shadowColor = active ? 'rgba(0, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0.65)';
      ctx.shadowBlur = active ? 8 * dpr : 6 * dpr;
      ctx.beginPath();
      ctx.arc(x, Math.floor(H * 0.5), active ? knobRActive : knobR, 0, Math.PI * 2);
      ctx.fillStyle = active ? '#4dffcd' : '#4dffcd';
      ctx.fill();
      ctx.restore();

      if (active) drawBubble(x, this.formatTime(timeSec));
    };

    const startActive = (this as any).hoverTrim === 'start' || (this as any).draggingTrim === 'start';
    const endActive = (this as any).hoverTrim === 'end' || (this as any).draggingTrim === 'end';
    drawHandle(xStart, startActive, trimStart);
    drawHandle(xEnd, endActive, trimEnd);

    // curseur de lecture (seul élément qui bouge en lecture)
    if (this.duration > 0) {
      const t = Math.min(Math.max(this.currentTime || 0, trimStart), trimEnd);
      const ratio = Math.max(0, Math.min(1, t / this.duration));
      const x = Math.floor(ratio * W);

      // 1) Bande de contraste derrière le curseur
      ctx.save();
      ctx.globalAlpha = 0.75;
      ctx.fillStyle = '#000';
      ctx.fillRect(x - 4 * dpr, 0, 8 * dpr, H);
      ctx.restore();

      // 2) Halo cyan autour de la ligne
      ctx.save();
      ctx.shadowColor = '#5fc9e4';
      ctx.shadowBlur = 10 * dpr;
      ctx.fillStyle = '#5fc9e4';
      ctx.fillRect(x - 1 * dpr, 0, 2 * dpr, H);
      ctx.restore();

      // 3) Liseré clair au centre (net)
      ctx.fillStyle = '#c8f2ff';
      ctx.fillRect(x - 0.5 * dpr, 0, 1 * dpr, H);
    }
  }

  private seekFromCanvasEvent(e: MouseEvent | TouchEvent): void {
    const canvas = this.$refs.spectrumCanvas as HTMLCanvasElement;
    const audio = this.$refs.audioPlayer as HTMLAudioElement;
    if (!canvas || !audio || !this.duration) return;

    const rect = canvas.getBoundingClientRect();
    const clientX = (e as TouchEvent).touches?.[0]?.clientX ?? (e as MouseEvent).clientX;
    const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
    const ratio = x / rect.width;
    const t = ratio * this.duration;

    // Seek clampé dans la plage
    const clamped = Math.min(Math.max(t, this.trimStart), this.trimEnd);
    audio.currentTime = clamped;
    this.currentTime = clamped;
    this.drawSpectrum(false);
  }

  // ====== Helpers TRIM ======
  private pxToTime(x: number, canvas: HTMLCanvasElement): number {
    const rect = canvas.getBoundingClientRect();
    const ratio = Math.min(Math.max(x / rect.width, 0), 1);
    return ratio * (this.duration || 0);
  }

  private handleHit(px: number, canvas: HTMLCanvasElement): 'start' | 'end' | null {
    if (!this.duration) return null;
    const { xStart, xEnd } = this.getSelectionBoundsPx(canvas);
    const tol = 10; // px de tolérance
    if (Math.abs(px - xStart) <= tol) return 'start';
    if (Math.abs(px - xEnd) <= tol) return 'end';
    return null;
  }

  private commitTrimToModel() {
    const s = this.ensureStab() as any;
    s.musicStartMs = Math.round(this.trimStart * 1000);
    s.musicEndMs = Math.round(this.trimEnd * 1000);
  }

  // ====== Events souris (nouvelle API) ======
  public onSpectrumDown(e: MouseEvent): void {
    const canvas = this.$refs.spectrumCanvas as HTMLCanvasElement;
    if (!canvas) return;
    const { rect } = this.getSelectionBoundsPx(canvas);
    const x = e.clientX - rect.left;

    const hit = this.handleHit(x, canvas);
    if (hit) {
      this.draggingTrim = hit;
      this.setCanvasCursor('ew-resize');
      return;
    }

    // sinon = seek dans la zone (clampé à la plage)
    this.seekingOnCanvas = true;
    this.setCanvasCursor('grabbing');
    this.seekFromCanvasEvent(e);
  }

  public onSpectrumMove(e: MouseEvent): void {
    const canvas = this.$refs.spectrumCanvas as HTMLCanvasElement;
    if (!canvas) return;

    const { rect } = this.getSelectionBoundsPx(canvas);
    const x = e.clientX - rect.left;

    if (this.draggingTrim) {
      const t = this.pxToTime(x, canvas);
      if (this.draggingTrim === 'start') {
        this.trimStart = Math.max(0, Math.min(t, this.trimEnd - this.minTrimGap));
        if (this.currentTime < this.trimStart) {
          this.currentTime = this.trimStart;
          const audio = this.$refs.audioPlayer as HTMLAudioElement;
          if (audio) audio.currentTime = this.currentTime;
        }
      } else {
        this.trimEnd = Math.max(this.trimStart + this.minTrimGap, Math.min(t, this.duration || t));
        if (this.currentTime > this.trimEnd) {
          this.currentTime = this.trimEnd;
          const audio = this.$refs.audioPlayer as HTMLAudioElement;
          if (audio) audio.currentTime = this.currentTime;
        }
      }
      this.commitTrimToModel();
      this.drawSpectrum(false);
      return;
    }

    if (this.seekingOnCanvas) {
      this.seekFromCanvasEvent(e);
      return;
    }

    // hover (changer le curseur)
    const hit = this.handleHit(x, canvas);
    this.hoverTrim = hit;
    if (hit) {
      this.setCanvasCursor('ew-resize');
    } else if (this.isInsideSelection(x, canvas)) {
      this.setCanvasCursor('pointer');
    } else {
      this.setCanvasCursor('default');
    }

    // redraw “léger” pour gérer l’état visuel des poignées
    this.drawSpectrum(false);
  }

  public onSpectrumUp(): void {
    this.seekingOnCanvas = false;
    this.draggingTrim = null;
    this.setCanvasCursor('default');
  }

  // ====== Events tactiles ======
  public onSpectrumTouchStart(e: TouchEvent): void {
    const canvas = this.$refs.spectrumCanvas as HTMLCanvasElement;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.touches[0].clientX - rect.left;
    const hit = this.handleHit(x, canvas);
    if (hit) {
      this.draggingTrim = hit;
    } else {
      this.seekingOnCanvas = true;
      this.seekFromCanvasEvent(e as any);
    }
  }

  public onSpectrumTouchMove(e: TouchEvent): void {
    if (this.draggingTrim) {
      this.onSpectrumMove(e as any);
    } else if (this.seekingOnCanvas) {
      this.seekFromCanvasEvent(e as any);
    }
  }

  public onSpectrumTouchEnd(): void {
    this.seekingOnCanvas = false;
    this.draggingTrim = null;
  }

  // ====== Compat : anciens handlers redirigés (si ton template les utilise encore) ======
  public onSpectrumPointerDown(e: MouseEvent) {
    this.onSpectrumDown(e);
  }
  public onSpectrumPointerMove(e: MouseEvent) {
    this.onSpectrumMove(e);
  }
  public onSpectrumPointerUp() {
    this.onSpectrumUp();
  }

  public async togglePreview(): Promise<void> {
    const audio = this.$refs.audioPlayer as HTMLAudioElement | undefined;
    if (!audio) return;

    this.ensureAudioGraph();
    try {
      await this.getAudioCtx().resume();
    } catch {
      // ignore
    }

    if (this.isPlaying) {
      audio.pause();
      this.isPlaying = false;
      this.stopSpectrum();
    } else {
      // Démarre au début de la plage si hors plage
      if (audio.currentTime < this.trimStart || audio.currentTime > this.trimEnd) {
        audio.currentTime = this.trimStart;
      }
      const p = audio.play();
      if (p && typeof (p as any).then === 'function') {
        (p as Promise<void>)
          .then(() => {
            this.isPlaying = true;
            this.startSpectrum();
          })
          .catch(() => {
            //  ignore
          });
      } else {
        this.isPlaying = true;
        this.startSpectrum();
      }
    }
  }

  public selectByIndex(i: number): void {
    if (i < 0 || i >= this.musicOptions.length) return;
    this.currentTrackIndex = i;
    this.selectedTrack = this.musicOptions[i].value;
    this.onMusicSelected();
  }

  private teardownAudioGraph(): void {
    try {
      this.sourceNode?.disconnect();
    } catch {
      // ignore
    }
    try {
      this.analyser?.disconnect();
    } catch {
      // ignore
    }
    try {
      this.silentGain?.disconnect();
    } catch {
      // ignore
    }
    this.sourceNode = null;
    this.analyser = null;
    this.silentGain = null;
    this.freqData = null;
    cancelAnimationFrame(this.spectrumRAF);
    this.spectrumRAF = 0;
  }

  private seedMusicKeys(overwrite: boolean) {
    const s = this.ensureStab() as any;
    const defaults = {
      musicGainDb: 0,
      musicFadeInMs: 0,
      musicFadeOutMs: 0,
      musicLoop: false,
      musicStartMs: 0, // NEW
      musicEndMs: 0, // NEW (0 = init par onLoadedMetadata quand durée connue)
    } as const;

    (Object.keys(defaults) as Array<keyof typeof defaults>).forEach((k) => {
      if (overwrite || typeof s[k] === 'undefined') {
        this.$set(s, k as any, (defaults as any)[k]);
      }
    });
  }

  private syncMusicFromModel() {
    const s = this.ensureStab() as any;
    this.musicEnabled = !!s.musicEnabled;

    if (s.musicUrl) {
      this.selectedTrack = s.musicUrl;
      const idx = this.musicOptions.findIndex((opt) => opt.value === this.selectedTrack);
      if (idx >= 0) this.currentTrackIndex = idx;
    }

    // Si le modèle avait déjà une plage, on l’applique (sera revalidé au chargement de la piste)
    if (typeof s.musicStartMs === 'number') this.trimStart = Math.max(0, s.musicStartMs / 1000);
    if (typeof s.musicEndMs === 'number' && s.musicEndMs > 0)
      this.trimEnd = Math.max(this.trimStart + this.minTrimGap, s.musicEndMs / 1000);
  }

  public onPickMusic(i: number): void {
    const opt = this.filteredMusic[i];
    if (!opt) return;

    const idx = this.musicOptions.findIndex((o) => o.value === opt.value);
    if (idx < 0) return;

    this.currentTrackIndex = idx;
    this.selectedTrack = opt.value;

    const s = this.ensureStab() as any;
    s.musicUrl = this.selectedTrack || undefined;
    s.musicTitle = opt.text || undefined;

    // Reset de la plage pour nouvelle piste (onLoadedMetadata refera l'init)
    s.musicStartMs = 0;
    s.musicEndMs = 0;

    const wasPlaying = this.isPlaying;
    this.isPlaying = false;
    this.currentTime = 0;
    this.$nextTick(() => {
      if (wasPlaying) this.togglePreview();
      else this.drawSpectrum(false);
    });
  }

  public toggleGenre(g: string): void {
    const i = this.selectedGenres.indexOf(g);
    if (i >= 0) this.selectedGenres.splice(i, 1);
    else this.selectedGenres.push(g);
  }

  public isGenreActive(g: string): boolean {
    return this.selectedGenres.includes(g);
  }

  public clearGenres(): void {
    this.selectedGenres = [];
  }

  @Watch('musicOpen', { immediate: true })
  onMusicOpenChanged(v: boolean) {
    if (!v) {
      // 🔻 Fermeture : tout couper
      const audio = this.$refs.audioPlayer as HTMLAudioElement | undefined;
      try {
        audio?.pause();
      } catch {
        // ignore
      }
      this.isPlaying = false;

      this.teardownAudioGraph(); // coupe source/analyser/gain + RAF
      this.spectrumRo?.disconnect(); // arrête le ResizeObserver du canvas
      this.spectrumRo = null;
      this.clearWaveform(); // libère les buffers min/max
      try {
        this.audioCtx?.suspend();
      } catch {
        // ignore
      } // économise le CPU
      return;
    }

    // 🔺 Ouverture : init "on demand" UNIQUEMENT si la musique est activée
    if (this.musicEnabled) {
      this.seedMusicKeys(false);
      if (!this.musicOptions.length) void this.fetchMusicLibrary();

      this.$nextTick(() => {
        const a = this.$refs.audioPlayer as HTMLAudioElement | undefined;
        a?.load();
        this.ensureAudioGraph();

        const canvas = this.$refs.spectrumCanvas as HTMLCanvasElement | undefined;
        if (canvas) {
          if (!this.spectrumRo) {
            this.spectrumRo = new ResizeObserver(() => {
              if (!this.musicOpen) return;
              this.syncSpectrumSize();
              this.drawSpectrum(false);
            });
          }
          this.spectrumRo.observe(canvas);
          this.syncSpectrumSize();
          this.drawSpectrum(false);
        }
      });
    }
  }

  @Watch('value', { deep: true })
  onValueChanged(): void {
    const s = this.ensureStab();
    if (typeof s.enabled === 'undefined') s.enabled = false;
    if (s.enabled) this.seedStabKeys(false);
  }

  @Watch('camera', { deep: true, immediate: true })
  async onCameraChanged() {
    if (this.camera?.lastPhoto) {
      await this.loadPreview();
      this.showPreview = false;
    }
  }
}
